import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  doc,
  getDoc,
  limit,
  startAfter,
  where,
} from "firebase/firestore";
import { firestoreProd } from "../../firebase";
import {
  FaSearch,
  FaChevronDown,
  FaChevronRight,
  FaListAlt,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaSync,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Add some CSS for the accordion
const accordionStyles = `
    .accordion-button:focus {
        box-shadow: none;
    }
    .accordion-arrow {
        transition: transform 0.2s ease;
    }
    .accordion-button[aria-expanded="true"] .accordion-arrow {
        transform: rotate(180deg);
    }
    .accordion-button {
        color: #495057 !important;
    }
    .accordion-button:hover {
        background-color: #f8f9fa;
    }
    .success-row {
        background-color: rgba(34, 197, 94, 0.05);
    }
    .fa-spin {
        animation: fa-spin 1s infinite linear;
    }
    @keyframes fa-spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;

// Array of queues to show - edit this array to add/remove queues
const ALLOWED_QUEUES = [
  "extract-document-text-content",
  "classify-document-group",
  "classify-document",
  "run-loan-processing-checklist-for-one-task",
];

const QueuePage = () => {
  const [queueLogs, setQueueLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);
  
  const BATCH_SIZE = 1000;

  // Compute filtered logs based on search input
  const filteredLogs = queueLogs.filter((log) => {
    if (!searchTerm.trim()) return true;

    const searchLower = searchTerm.toLowerCase();
    const queueIdMatch = log.queueId?.toLowerCase().includes(searchLower);
    const orgIdMatch = log.organizationId?.toLowerCase().includes(searchLower);
    const orgNameMatch = log.organizationName?.toLowerCase().includes(searchLower);
    const bodyMatch = log.body?.toLowerCase().includes(searchLower);

    return queueIdMatch || orgIdMatch || orgNameMatch || bodyMatch;
  });

  const fetchOrganizations = useCallback(async (organizationIds) => {
    const organizationsMap = {};

    if (!organizationIds || organizationIds.length === 0) {
      return organizationsMap;
    }

    try {
      // Fetch each organization by ID
      const fetchPromises = organizationIds.map(async (orgId) => {
        try {
          const orgRef = doc(firestoreProd, "organizations", orgId);
          const orgSnapshot = await getDoc(orgRef);

          if (orgSnapshot.exists()) {
            const orgData = orgSnapshot.data();
            return { id: orgId, name: orgData.name || orgId };
          }
          return { id: orgId, name: null };
        } catch (error) {
          console.warn(`Error fetching organization ${orgId}:`, error);
          return { id: orgId, name: null };
        }
      });

      const orgResults = await Promise.all(fetchPromises);

      // Build map of organizationId -> organizationName
      orgResults.forEach(({ id, name }) => {
        organizationsMap[id] = name;
      });

      console.log("Organizations map:", organizationsMap);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }

    return organizationsMap;
  }, []);

  const fetchQueueLogs = useCallback(async (loadMore = false) => {
    if (!firestoreProd) {
      toast.error("Firebase is not initialized");
      return;
    }

    // Prevent loading more if we're already loading or there's no more data
    if (loadMore && (isLoadingMore || !hasMore)) {
      return;
    }

    if (loadMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setError(null);
      setQueueLogs([]);
      setLastDoc(null);
      setHasMore(true);
      setHasLoaded(true);
    }

    try {
      const logsRef = collection(firestoreProd, "queue-logs");
      
      // Build query with pagination and filtering in the database
      let q;
      if (loadMore && lastDoc) {
        q = query(
          logsRef,
          where("queueId", "in", ALLOWED_QUEUES),
          orderBy("createdAt", "desc"),
          startAfter(lastDoc),
          limit(BATCH_SIZE)
        );
      } else {
        q = query(
          logsRef,
          where("queueId", "in", ALLOWED_QUEUES),
          orderBy("createdAt", "desc"),
          limit(BATCH_SIZE)
        );
      }
      
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        if (!loadMore) {
          toast.info("No queue logs found");
          setQueueLogs([]);
        }
        setHasMore(false);
        return;
      }

      // Check if we have more data
      const hasMoreData = querySnapshot.docs.length === BATCH_SIZE;
      setHasMore(hasMoreData);

      // Store the last document for pagination
      if (querySnapshot.docs.length > 0) {
        setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
      }

      const logsList = [];
      const organizationIds = new Set();

      for (const docSnapshot of querySnapshot.docs) {
        const logData = docSnapshot.data();

        // No need to filter anymore - filtering is done in the database
        logsList.push({
          id: docSnapshot.id,
          ...logData,
        });

        // Collect unique organization IDs
        if (logData.organizationId) {
          organizationIds.add(logData.organizationId);
        }
      }

      // Fetch all organizations
      console.log("Fetching organizations:", Array.from(organizationIds));
      const organizationsMap = await fetchOrganizations(
        Array.from(organizationIds)
      );

      // Add organization names to logs
      const logsWithOrgNames = logsList.map((log) => ({
        ...log,
        organizationName: log.organizationId
          ? organizationsMap[log.organizationId] || null
          : null,
      }));

      if (loadMore) {
        setQueueLogs((prev) => [...prev, ...logsWithOrgNames]);
        toast.success(`Loaded ${logsWithOrgNames.length} more queue logs`);
      } else {
        setQueueLogs(logsWithOrgNames);
        toast.success(`Loaded ${logsWithOrgNames.length} queue logs`);
      }
    } catch (error) {
      console.error("Error fetching queue logs:", error);
      setError("Failed to fetch queue logs");
      toast.error("Failed to fetch queue logs");
    } finally {
      if (loadMore) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [fetchOrganizations, lastDoc, hasMore, isLoadingMore, BATCH_SIZE]);

  // Load initial logs on component mount
  useEffect(() => {
    fetchQueueLogs(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !isLoadingMore && !isLoading) {
          console.log("Loading more logs...");
          fetchQueueLogs(true);
        }
      },
      {
        root: null,
        rootMargin: "100px", // Start loading 100px before reaching the bottom
        threshold: 0.1,
      }
    );

    const currentLoaderRef = loaderRef.current;
    if (currentLoaderRef) {
      observer.observe(currentLoaderRef);
    }

    return () => {
      if (currentLoaderRef) {
        observer.unobserve(currentLoaderRef);
      }
    };
  }, [hasMore, isLoadingMore, isLoading, fetchQueueLogs]);

  // Auto-load more data if filtered results are too few and we have more data available
  useEffect(() => {
    // If we're searching and have very few results, automatically load more
    if (
      searchTerm.trim() &&
      filteredLogs.length < 20 &&
      queueLogs.length > 0 &&
      hasMore &&
      !isLoadingMore &&
      !isLoading
    ) {
      console.log("Auto-loading more data due to few filtered results...");
      fetchQueueLogs(true);
    }
  }, [searchTerm, filteredLogs.length, queueLogs.length, hasMore, isLoadingMore, isLoading, fetchQueueLogs]);

  const toggleLogExpansion = (logId) => {
    setExpandedLogs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "N/A";
    const now = Date.now();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return `${seconds}s ago`;
  };

  const calculateDelay = (startTime, endTime) => {
    if (!startTime || !endTime) return { text: "N/A", isWarning: false };
    const diff = endTime - startTime;
    const totalSeconds = Math.floor(diff / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const isWarning = minutes > 5;

    if (minutes > 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return { text: `${hours}h ${remainingMinutes}m`, isWarning };
    }
    if (minutes > 0) {
      return { text: `${minutes}m ${seconds}s`, isWarning };
    }
    return { text: `${seconds}s`, isWarning };
  };

  const renderSuccessBadge = (success) => {
    if (success === true) {
      return (
        <span className="badge bg-success">
          <FaCheckCircle className="me-1" />
          Success
        </span>
      );
    } else if (success === false) {
      return (
        <span className="badge bg-danger">
          <FaTimesCircle className="me-1" />
          Failed
        </span>
      );
    } else {
      return (
        <span className="badge bg-warning">
          <FaClock className="me-1" />
          Pending
        </span>
      );
    }
  };

  const formatJSON = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return jsonString;
    }
  };

  return (
    <div className="container-fluid p-4 QueuePageContainer">
      <style dangerouslySetInnerHTML={{ __html: accordionStyles }} />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <h1>
        <FaListAlt className="me-2" />
        Queue Logs
        <button
          className="btn btn-primary btn-sm ms-3"
          onClick={() => fetchQueueLogs(false)}
          disabled={isLoading || isLoadingMore}
          title="Reload queue logs"
        >
          <FaSync className={`me-1 ${isLoading ? 'fa-spin' : ''}`} />
          Reload
        </button>
      </h1>

      {/* Instructions Section - Collapsible */}
      <div className="card mb-4">
        <div className="card-header">
          <h2 className="h5 mb-0">
            <button
              className="accordion-button collapsed p-2 text-decoration-none w-100 d-flex justify-content-between align-items-center border-0"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#instructionsCollapse"
              aria-expanded="false"
              aria-controls="instructionsCollapse"
            >
              <span>📖 How to View Queue Logs</span>
              <span className="accordion-arrow">▼</span>
            </button>
          </h2>
        </div>
        <div className="collapse" id="instructionsCollapse">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <h6>✅ How to Use:</h6>
                <ol>
                  <li>
                    Queue logs are loaded in batches of 100 items when you open this
                    page
                  </li>
                  <li>Logs are sorted by creation date (newest first)</li>
                  <li>
                    Scroll to the bottom to automatically load more logs
                  </li>
                  <li>
                    Use the search box to filter by queue name, organization ID,
                    or body content
                  </li>
                  <li>Click on any log row to see full details</li>
                  <li>
                    Each log shows key metrics: success status, delays, and
                    execution count
                  </li>
                </ol>
              </div>
              <div className="col-md-6">
                <h6>🔍 Search Options:</h6>
                <ul>
                  <li>Queue name</li>
                  <li>Organization name or ID</li>
                  <li>Body content</li>
                </ul>
                <h6 className="mt-3">📊 Queue Log Information:</h6>
                <ul>
                  <li>
                    <strong>Queue Name:</strong> The name of the queue
                  </li>
                  <li>
                    <strong>Success:</strong> Status badge (green = success, red
                    = failed, yellow = pending)
                  </li>
                  <li>
                    <strong>Delay to Start:</strong> Time between creation and
                    start (red if &gt; 5 minutes)
                  </li>
                  <li>
                    <strong>Delay to Finish:</strong> Time between start and
                    completion (red if &gt; 5 minutes)
                  </li>
                  <li>
                    <strong>Total Delay:</strong> Total time from creation to
                    completion (red if &gt; 5 minutes)
                  </li>
                  <li>
                    <strong>Executions:</strong> Number of execution attempts
                    (red if &gt; 1)
                  </li>
                  <li>
                    <strong>Created At:</strong> Time ago format
                  </li>
                </ul>
              </div>
            </div>

            <div className="alert alert-info mt-3" role="alert">
              <strong>💡 Tip:</strong> Rows with successful executions have a
              slight green background. Click on any row to expand and see
              complete execution history.
            </div>
          </div>
        </div>
      </div>

      {/* Queue Logs List */}
      <div className="card mb-4">
        <div className="card-header">
          <h2 className="h5 mb-0">Queue Executions</h2>
          <small className="text-muted">View queue execution history</small>
          {isLoading && (
            <div className="mt-2">
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              ></div>
              <span className="text-muted">Loading queue logs...</span>
            </div>
          )}
          {queueLogs.length > 0 && (
            <div className="mt-2">
              <span className="text-muted">
                Total logs: {queueLogs.length}
                {searchTerm.trim() && (
                  <span> | Filtered: {filteredLogs.length}</span>
                )}
              </span>
            </div>
          )}
        </div>
        <div className="card-body">
          {/* Real-time search input for table */}
          {queueLogs.length > 0 && (
            <div className="mb-3">
              <div className="row">
                <div className="col-md-6">
                  <label htmlFor="logSearch" className="form-label">
                    Filter Queue Logs
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      id="logSearch"
                      placeholder="Filter by queue name, organization ID/name, or body content"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setSearchTerm("")}
                        title="Clear filter"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {searchTerm && (
                    <small className="text-muted">
                      Showing {filteredLogs.length} of {queueLogs.length} logs
                    </small>
                  )}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {/* Logs Table with Accordion */}
          {filteredLogs.length > 0 && (
            <div>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      <th width="50"></th>
                      <th>Queue Name</th>
                      <th>Organization</th>
                      <th>Success</th>
                      <th>Delay to Start</th>
                      <th>Delay to Finish</th>
                      <th>Total Delay</th>
                      <th>Executions</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log) => {
                      const isExpanded = expandedLogs.has(log.id);
                      const executionCount = log.executions?.length || 0;
                      const delayToStart = calculateDelay(
                        log.createdAt,
                        log.startedAt
                      );
                      const delayToFinish = calculateDelay(
                        log.startedAt,
                        log.finishedAt
                      );
                      const totalDelay = calculateDelay(
                        log.createdAt,
                        log.finishedAt
                      );

                      return (
                        <React.Fragment key={log.id}>
                          {/* Main Row - Clickable */}
                          <tr
                            className={`cursor-pointer ${
                              log.success === true ? "success-row" : ""
                            }`}
                            onClick={() => toggleLogExpansion(log.id)}
                            style={{ cursor: "pointer" }}
                          >
                            <td>
                              {isExpanded ? (
                                <FaChevronDown className="text-muted" />
                              ) : (
                                <FaChevronRight className="text-muted" />
                              )}
                            </td>
                            <td>
                              <strong>{log.queueId || "N/A"}</strong>
                            </td>
                            <td>
                              {log.organizationName ? (
                                <div>{log.organizationName}</div>
                              ) : log.organizationId ? (
                                <small className="text-muted">Unknown Org</small>
                              ) : (
                                <span className="text-muted">N/A</span>
                              )}
                            </td>
                            <td>{renderSuccessBadge(log.success)}</td>
                            <td>
                              <span
                                style={{
                                  fontWeight: delayToStart.isWarning
                                    ? "bold"
                                    : "normal",
                                  color: delayToStart.isWarning
                                    ? "#dc3545"
                                    : "inherit",
                                }}
                              >
                                {delayToStart.text}
                              </span>
                            </td>
                            <td>
                              <span
                                style={{
                                  fontWeight: delayToFinish.isWarning
                                    ? "bold"
                                    : "normal",
                                  color: delayToFinish.isWarning
                                    ? "#dc3545"
                                    : "inherit",
                                }}
                              >
                                {delayToFinish.text}
                              </span>
                            </td>
                            <td>
                              <span
                                style={{
                                  fontWeight: totalDelay.isWarning
                                    ? "bold"
                                    : "normal",
                                  color: totalDelay.isWarning
                                    ? "#dc3545"
                                    : "inherit",
                                }}
                              >
                                {totalDelay.text}
                              </span>
                            </td>
                            <td>
                              <span
                                style={{
                                  fontWeight:
                                    executionCount > 1 ? "bold" : "normal",
                                  color:
                                    executionCount > 1 ? "#dc3545" : "inherit",
                                }}
                              >
                                {executionCount}
                              </span>
                            </td>
                            <td>
                              <FaClock className="me-1 text-muted" />
                              {formatTimeAgo(log.createdAt)}
                            </td>
                          </tr>

                          {/* Expanded Row - Details */}
                          {isExpanded && (
                            <tr>
                              <td colSpan="9" className="bg-light">
                                <div className="p-3">
                                  <h6 className="mb-3">
                                    <FaListAlt className="me-2" />
                                    Queue Execution Details
                                  </h6>

                                  {/* Basic Information */}
                                  <div className="row mb-3">
                                    <div className="col-md-6">
                                      <div className="card">
                                        <div className="card-body">
                                          <h6 className="card-subtitle mb-2 text-muted">
                                            Basic Information
                                          </h6>
                                          <ul className="list-unstyled">
                                            <li>
                                              <strong>Record ID:</strong>{" "}
                                              <small className="font-monospace">
                                                {log.id}
                                              </small>
                                            </li>
                                            <li>
                                              <strong>Queue ID:</strong>{" "}
                                              {log.queueId || "N/A"}
                                            </li>
                                            {log.organizationId && (
                                              <li>
                                                <strong>Organization ID:</strong>{" "}
                                                <small className="font-monospace">
                                                  {log.organizationId}
                                                </small>
                                              </li>
                                            )}
                                            <li>
                                              <strong>Created At:</strong>{" "}
                                              {formatTimestamp(log.createdAt)}
                                            </li>
                                            {log.startedAt && (
                                              <li>
                                                <strong>Started At:</strong>{" "}
                                                {formatTimestamp(log.startedAt)}
                                              </li>
                                            )}
                                            {log.finishedAt && (
                                              <li>
                                                <strong>Finished At:</strong>{" "}
                                                {formatTimestamp(log.finishedAt)}
                                              </li>
                                            )}
                                            <li>
                                              <strong>Success:</strong>{" "}
                                              {renderSuccessBadge(log.success)}
                                            </li>
                                          </ul>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="card">
                                        <div className="card-body">
                                          <h6 className="card-subtitle mb-2 text-muted">
                                            Payload Body
                                          </h6>
                                          <pre
                                            style={{
                                              backgroundColor: "#f8f9fa",
                                              padding: "10px",
                                              borderRadius: "4px",
                                              maxHeight: "200px",
                                              overflow: "auto",
                                              fontSize: "12px",
                                            }}
                                          >
                                            {log.body
                                              ? formatJSON(log.body)
                                              : "No body"}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Executions */}
                                  {log.executions && log.executions.length > 0 && (
                                    <div>
                                      <h6 className="mb-3">
                                        Execution History ({log.executions.length}{" "}
                                        {log.executions.length === 1
                                          ? "attempt"
                                          : "attempts"}
                                        )
                                      </h6>
                                      <div className="list-group">
                                        {log.executions.map((execution, index) => {
                                          const execDelay = calculateDelay(
                                            execution.startedAt,
                                            execution.finishedAt
                                          );

                                          return (
                                            <div
                                              key={index}
                                              className="list-group-item mb-2"
                                              style={{
                                                border: "1px solid #dee2e6",
                                                borderRadius: "4px",
                                                backgroundColor:
                                                  execution.success === true
                                                    ? "rgba(34, 197, 94, 0.05)"
                                                    : execution.success === false
                                                    ? "rgba(220, 53, 69, 0.05)"
                                                    : "transparent",
                                              }}
                                            >
                                              <div className="d-flex justify-content-between align-items-start">
                                                <div className="flex-grow-1">
                                                  {/* Header with badge and status */}
                                                  <div className="d-flex align-items-center mb-3">
                                                    <span className="badge bg-secondary me-2">
                                                      Attempt {index + 1}
                                                    </span>
                                                    {renderSuccessBadge(
                                                      execution.success
                                                    )}
                                                  </div>

                                                  {/* Timestamps */}
                                                  <div className="mb-3">
                                                    <div className="mb-1">
                                                      <strong>Started At:</strong>{" "}
                                                      <span className="text-muted">
                                                        <FaClock className="me-1" />
                                                        {formatTimestamp(
                                                          execution.startedAt
                                                        )}
                                                      </span>
                                                    </div>
                                                    {execution.finishedAt && (
                                                      <div className="mb-1">
                                                        <strong>Finished At:</strong>{" "}
                                                        <span className="text-muted">
                                                          <FaClock className="me-1" />
                                                          {formatTimestamp(
                                                            execution.finishedAt
                                                          )}
                                                        </span>
                                                      </div>
                                                    )}
                                                    {execution.startedAt && execution.finishedAt && (
                                                      <div className="mb-1">
                                                        <strong>Duration:</strong>{" "}
                                                        <span className="text-muted">
                                                          {execDelay.text}
                                                        </span>
                                                      </div>
                                                    )}
                                                  </div>

                                                  {/* Success Status */}
                                                  <div className="mb-3">
                                                    <strong>Success:</strong>{" "}
                                                    {execution.success !== undefined
                                                      ? execution.success.toString()
                                                      : "N/A"}
                                                  </div>

                                                  {/* Message */}
                                                  {execution.message && (
                                                    <div className="mb-3">
                                                      <strong>Message:</strong>
                                                      <div
                                                        style={{
                                                          backgroundColor: "#f8f9fa",
                                                          padding: "8px",
                                                          borderRadius: "4px",
                                                          marginTop: "5px",
                                                        }}
                                                      >
                                                        {execution.message}
                                                      </div>
                                                    </div>
                                                  )}

                                                  {/* Error Message (if different from message) */}
                                                  {execution.errorMessage && (
                                                    <div className="mb-3">
                                                      <strong className="text-danger">
                                                        Error Message:
                                                      </strong>
                                                      <div
                                                        style={{
                                                          backgroundColor: "#fff3cd",
                                                          padding: "8px",
                                                          borderRadius: "4px",
                                                          marginTop: "5px",
                                                          color: "#842029",
                                                        }}
                                                      >
                                                        {execution.errorMessage}
                                                      </div>
                                                    </div>
                                                  )}

                                                  {/* Response */}
                                                  {execution.response && (
                                                    <div className="mb-3">
                                                      <strong className="text-muted">
                                                        Response:
                                                      </strong>
                                                      <pre
                                                        style={{
                                                          backgroundColor:
                                                            "#f8f9fa",
                                                          padding: "10px",
                                                          borderRadius: "4px",
                                                          marginTop: "5px",
                                                          maxHeight: "300px",
                                                          overflow: "auto",
                                                          fontSize: "12px",
                                                        }}
                                                      >
                                                        {execution.response}
                                                      </pre>
                                                    </div>
                                                  )}

                                                  {/* Stack Trace */}
                                                  {execution.stack && (
                                                    <div className="mb-3">
                                                      <strong className="text-danger">
                                                        Stack Trace:
                                                      </strong>
                                                      <pre
                                                        style={{
                                                          backgroundColor:
                                                            "#fff3cd",
                                                          padding: "10px",
                                                          borderRadius: "4px",
                                                          marginTop: "5px",
                                                          maxHeight: "400px",
                                                          overflow: "auto",
                                                          fontSize: "11px",
                                                          color: "#842029",
                                                          fontFamily: "monospace",
                                                        }}
                                                      >
                                                        {execution.stack}
                                                      </pre>
                                                    </div>
                                                  )}

                                                  {/* Show any other fields that might exist */}
                                                  {Object.keys(execution).filter(
                                                    key => !['startedAt', 'finishedAt', 'success', 'message', 'errorMessage', 'response', 'stack'].includes(key)
                                                  ).length > 0 && (
                                                    <div className="mb-3">
                                                      <strong className="text-muted">
                                                        Additional Fields:
                                                      </strong>
                                                      <div
                                                        style={{
                                                          backgroundColor: "#f8f9fa",
                                                          padding: "10px",
                                                          borderRadius: "4px",
                                                          marginTop: "5px",
                                                        }}
                                                      >
                                                        <ul className="list-unstyled mb-0">
                                                          {Object.entries(execution)
                                                            .filter(
                                                              ([key]) => !['startedAt', 'finishedAt', 'success', 'message', 'errorMessage', 'response', 'stack'].includes(key)
                                                            )
                                                            .map(([key, value]) => (
                                                              <li key={key}>
                                                                <strong>{key}:</strong>{" "}
                                                                {typeof value === "object"
                                                                  ? JSON.stringify(value, null, 2)
                                                                  : String(value)}
                                                              </li>
                                                            ))}
                                                        </ul>
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}

                                  {(!log.executions || log.executions.length === 0) && (
                                    <div className="alert alert-info" role="alert">
                                      No execution history found for this queue log.
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {filteredLogs.length === 0 &&
            queueLogs.length > 0 &&
            searchTerm.trim() && (
              <div className="alert alert-info" role="alert">
                <FaSearch className="me-2" />
                No logs found matching "{searchTerm}". Try a different search
                term.
              </div>
            )}

          {/* Infinite scroll loader - Always render so it can trigger loading more data */}
          {queueLogs.length > 0 && (
            <div ref={loaderRef} className="text-center py-4">
              {isLoadingMore && (
                <div>
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  ></div>
                  <span className="text-muted">Loading more logs...</span>
                </div>
              )}
              {!hasMore && !isLoadingMore && (
                <div className="text-muted">
                  <small>No more logs to load</small>
                </div>
              )}
              {hasMore && !isLoadingMore && filteredLogs.length === 0 && searchTerm.trim() && (
                <div className="text-muted">
                  <small>Scroll down to load more results...</small>
                </div>
              )}
            </div>
          )}

          {queueLogs.length === 0 && hasLoaded && !isLoading && (
            <div className="alert alert-info" role="alert">
              No queue logs found.
            </div>
          )}

          {!hasLoaded && !isLoading && (
            <div className="alert alert-info" role="alert">
              Loading queue logs...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueuePage;

